import { Link } from "react-router-dom";
import { Article, CATEGORY_CONFIG } from "@/data/mockData";
import { Clock, ArrowRight, AlertTriangle, Newspaper, Building2, Landmark, Scale, Star, BarChart3, TrendingUp, Trophy, Shield } from "lucide-react";
import Sparkline from "./Sparkline";

interface ArticleCardProps {
  article: Article;
  index: number;
}

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  prop_firm: Building2,
  broker: Landmark,
  fintech: TrendingUp,
  regulation: Shield,
  review: Star,
  comparison: Scale,
  market_analysis: BarChart3,
  awards: Trophy,
  expose: AlertTriangle,
  event: Newspaper,
};

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function getEntityInitials(name: string): string {
  return name.split(" ").map(w => w[0]).join("").slice(0, 3).toUpperCase();
}

/**
 * CSS-generated visual fallback for cards without a banner image.
 * Phase 1 cleanup: dropped the per-entity rainbow color map. All entity
 * blocks now use the same neutral surface, with category accent reserved
 * for the icon only — keeps the editorial palette disciplined.
 */
const ArticleVisual = ({ article }: { article: Article }) => {
  const config = CATEGORY_CONFIG[article.category];
  const CategoryIcon = CATEGORY_ICONS[article.category] || Newspaper;

  // Review: star rating
  if (article.category === "review" && article.rating) {
    const fullStars = Math.floor(article.rating);
    return (
      <div className="relative h-44 flex flex-col items-center justify-center bg-surface-elevated">
        <div className="absolute inset-0 bg-grid-pattern opacity-15" />
        <div className="relative z-10 flex flex-col items-center gap-3">
          {article.entityName && (
            <span className="font-display text-2xl font-bold text-foreground">
              {getEntityInitials(article.entityName)}
            </span>
          )}
          <div className="flex items-center gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className={`h-5 w-5 ${i < fullStars ? "fill-wire-gold text-wire-gold" : "text-muted-foreground/30"}`}
              />
            ))}
          </div>
          <span className="font-display text-xl font-bold text-foreground">{article.rating}/5</span>
        </div>
      </div>
    );
  }

  // Comparison: VS split
  if (article.category === "comparison" && article.versus) {
    return (
      <div className="relative h-44 flex items-center justify-center bg-surface-elevated">
        <div className="absolute inset-0 bg-grid-pattern opacity-15" />
        <div className="relative z-10 flex items-center gap-4">
          <div className="flex flex-col items-center">
            <div className="h-14 w-14 rounded-full bg-secondary/15 flex items-center justify-center border border-border">
              <span className="font-display text-sm font-bold text-secondary">{getEntityInitials(article.versus[0])}</span>
            </div>
            <span className="text-xs text-muted-foreground mt-1.5 max-w-[80px] text-center truncate">{article.versus[0]}</span>
          </div>
          <div className="h-12 w-12 rounded-full bg-foreground/10 flex items-center justify-center border border-border">
            <span className="font-display text-sm font-bold text-foreground">VS</span>
          </div>
          <div className="flex flex-col items-center">
            <div className="h-14 w-14 rounded-full bg-primary/15 flex items-center justify-center border border-border">
              <span className="font-display text-sm font-bold text-primary">{getEntityInitials(article.versus[1])}</span>
            </div>
            <span className="text-xs text-muted-foreground mt-1.5 max-w-[80px] text-center truncate">{article.versus[1]}</span>
          </div>
        </div>
      </div>
    );
  }

  // Awards
  if (article.category === "awards") {
    return (
      <div className="relative h-44 flex flex-col items-center justify-center bg-surface-elevated">
        <div className="absolute inset-0 bg-grid-pattern opacity-15" />
        <div className="relative z-10 flex flex-col items-center gap-2">
          <div className="h-16 w-16 rounded-full bg-wire-gold/15 flex items-center justify-center">
            <Trophy className="h-8 w-8 text-wire-gold" />
          </div>
          <span className="font-display text-eyebrow text-wire-gold">Awards 2025</span>
        </div>
      </div>
    );
  }

  // Exposé / Regulation: alert visual
  if (article.category === "expose" || article.category === "regulation") {
    return (
      <div className="relative h-44 flex flex-col items-center justify-center bg-surface-elevated">
        <div className="absolute inset-0 bg-grid-pattern opacity-15" />
        <div className="relative z-10 flex flex-col items-center gap-2">
          {article.entityName ? (
            <>
              <div className="h-14 w-14 rounded-xl flex items-center justify-center border border-destructive/30 bg-card">
                <span className="font-display text-lg font-bold text-destructive">
                  {getEntityInitials(article.entityName)}
                </span>
              </div>
              <span className="font-display text-sm font-semibold text-foreground">
                {article.entityName}
              </span>
            </>
          ) : (
            <div className="h-16 w-16 rounded-full bg-destructive/15 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
          )}
          <span className="text-eyebrow text-destructive">{config.label}</span>
        </div>
      </div>
    );
  }

  // Market analysis: chart
  if (article.category === "market_analysis") {
    const sparkTrend = article.headline.toLowerCase().includes("surge") || article.headline.toLowerCase().includes("rally") ? "up" as const : "flat" as const;
    return (
      <div className="relative h-44 flex flex-col items-center justify-center bg-surface-elevated">
        <div className="absolute inset-0 bg-grid-pattern opacity-15" />
        <div className="relative z-10 flex flex-col items-center gap-2 w-full px-8">
          <BarChart3 className="h-8 w-8 text-secondary mb-1" />
          <div className="w-full h-16">
            <Sparkline trend={sparkTrend} className="w-full h-full" />
          </div>
          <span className="text-eyebrow text-secondary">Analysis</span>
        </div>
      </div>
    );
  }

  // Entity (broker/prop/fintech): clean neutral block, single accent
  if (article.entityName) {
    return (
      <div className="relative h-44 flex flex-col items-center justify-center bg-surface-elevated">
        <div className="absolute inset-0 bg-grid-pattern opacity-15" />
        <div className="relative z-10 flex flex-col items-center gap-3">
          <div className="h-16 w-16 rounded-xl flex items-center justify-center border border-border bg-card">
            <span className="font-display text-xl font-bold text-primary">
              {getEntityInitials(article.entityName)}
            </span>
          </div>
          <span className="font-display text-sm font-semibold text-foreground">
            {article.entityName}
          </span>
        </div>
      </div>
    );
  }

  // Default
  return (
    <div className="relative h-44 flex flex-col items-center justify-center bg-surface-elevated">
      <div className="absolute inset-0 bg-grid-pattern opacity-15" />
      <div className="relative z-10 flex flex-col items-center gap-2">
        <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
          <CategoryIcon className="h-8 w-8 text-muted-foreground" />
        </div>
        <span className="text-eyebrow text-muted-foreground">{config.label}</span>
      </div>
    </div>
  );
};

const ArticleCard = ({ article, index }: ArticleCardProps) => {
  const config = CATEGORY_CONFIG[article.category];
  const isHighImpact = article.impactLevel === "high";
  const CategoryIcon = CATEGORY_ICONS[article.category] || Newspaper;

  return (
    <Link to={`/news/${article.slug}`} className="block">
      <article
        className={`group card-premium overflow-hidden cursor-pointer animate-fade-in ${
          isHighImpact ? "ring-1 ring-destructive/25" : ""
        }`}
        style={{ animationDelay: `${Math.min(index * 50, 200)}ms`, animationFillMode: "both" }}
      >
        {/* CSS-generated visual or banner */}
        <ArticleVisual article={article} />

        {/* Category badge overlay — neutral surface, NOT a per-category color */}
        <div className="absolute top-4 left-4 z-10">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-2xs font-semibold uppercase tracking-wide glass-subtle text-foreground">
            <CategoryIcon className="h-3 w-3" />
            {config.label}
          </span>
        </div>

        {isHighImpact && (
          <div className="absolute top-4 right-4 z-10">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-2xs font-semibold bg-destructive text-destructive-foreground uppercase tracking-wide">
              <AlertTriangle className="h-3 w-3" />
              High Impact
            </span>
          </div>
        )}

        <div className="p-6">
          <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {timeAgo(article.createdAt)}
            </span>
            {article.entityName && <span>· {article.entityName}</span>}
            {article.rating && (
              <span className="flex items-center gap-0.5 text-wire-gold">
                · <Star className="h-3 w-3 fill-wire-gold" />
                {article.rating}
              </span>
            )}
          </div>

          <h3 className="font-display text-xl font-semibold leading-snug tracking-tight mb-3 group-hover:text-primary transition-colors">
            {article.headline}
          </h3>

          <p className="text-sm text-muted-foreground leading-relaxed mb-5 line-clamp-2">
            {article.excerpt}
          </p>

          <div className="flex items-center justify-between pt-4 border-t border-border/40">
            <div className="text-xs text-muted-foreground">
              <span className="text-foreground font-medium">{article.author}</span>
              {article.readTime && <span> · {article.readTime} read</span>}
            </div>
            <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:text-primary transition-all">
              Read <ArrowRight className="h-3 w-3" />
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
};

export default ArticleCard;
